import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AccountLayout from '@/components/feature/AccountLayout';
import Toast from '@/components/common/Toast';
import { useAuth } from '@/hooks/useAuth';
import { uploadFile } from '@/utils/upload';
import {
  fetchEnterpriseSettlementProfile,
  fetchUpdateEnterpriseSettlementProfile,
  fetchEnterpriseSettlementProfileReviewRecords,
  fetchPersonalSettlementProfile,
  fetchUpdatePersonalSettlementProfile,
  fetchPersonalSettlementProfileReviewRecords,
} from '@/service/api/revenue';
import { fetchGetPersonalCertification } from '@/service/api/personalCertification';
import { fetchGetEnterpriseMarketOnboarding } from '@/service/api/enterpriseOnboarding';

/**
 * 实名认证状态类型
 */
type CertStatus = 'unverified' | 'pending' | 'approved' | 'rejected';

/**
 * 附件列表项前端结构
 */
interface AttachmentItem {
  id?: string;
  display_name: string;
  upload_asset_id: string;
  visibility: Api.Revenue.AttachmentVisibility;
}

/**
 * 映射 API 状态字符串到前端 CertStatus
 */
const mapApiStatusToCertStatus = (status?: string): CertStatus => {
  if (!status) return 'unverified';
  const s = status.toUpperCase();
  if (s === 'PENDING' || s === 'AUDITING' || s === 'SUBMITTED') return 'pending';
  if (s === 'APPROVED' || s === 'PASSED' || s === 'SUCCESS') return 'approved';
  if (s === 'REJECTED' || s === 'FAILED') return 'rejected';
  return 'unverified';
};

/**
 * 结算资料审核状态展示配置
 */
const profileStatusConfig: Record<string, { label: string; className: string }> = {
  PENDING: { label: '审核中', className: 'bg-amber-500/10 text-amber-600 border border-amber-500/20' },
  AUDITING: { label: '审核中', className: 'bg-amber-500/10 text-amber-600 border border-amber-500/20' },
  SUBMITTED: { label: '已提交', className: 'bg-blue-500/10 text-blue-600 border border-blue-500/20' },
  APPROVED: { label: '已通过', className: 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20' },
  PASSED: { label: '已通过', className: 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20' },
  REJECTED: { label: '已驳回', className: 'bg-rose-500/10 text-rose-600 border border-rose-500/20' },
  UNSUBMITTED: { label: '未提交', className: 'bg-gray-500/10 text-gray-600 border border-gray-500/20' },
};

export default function BillingProfilePage() {
  const navigate = useNavigate();
  const { user, rawUser } = useAuth();

  // 根据 AuthStatus / 用户账号类型自动判断使用企业还是个人
  const isEnterpriseAccount =
    (rawUser?.account_type || '').toUpperCase() === 'ENTERPRISE';
  const accountType: 'enterprise' | 'personal' = isEnterpriseAccount ? 'enterprise' : 'personal';

  // 全局加载与提交状态
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [uploadingAttachment, setUploadingAttachment] = useState<boolean>(false);

  // 认证状态与拒绝原因
  const [certStatus, setCertStatus] = useState<CertStatus>('unverified');
  const [certRejectReason, setCertRejectReason] = useState<string>('');

  // 结算资料审核状态与退回说明
  const [profileStatus, setProfileStatus] = useState<string>('');
  const [auditRemark, setAuditRemark] = useState<string>('');

  // Toast 消息状态
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' | 'info' | 'warning' } | null>(null);

  // 审核记录弹窗状态
  const [showReviewModal, setShowReviewModal] = useState<boolean>(false);
  const [reviewRecords, setReviewRecords] = useState<Api.Revenue.ProfileReviewRecordResponse[]>([]);
  const [loadingRecords, setLoadingRecords] = useState<boolean>(false);

  // 企业结算资料表单及附件
  const [entForm, setEntForm] = useState({
    bank_account_name: '',
    bank_name: '',
    bank_branch_name: '',
    bank_account_no: '',
    tax_no: '',
    invoice_title: '',
    invoice_address_phone: '',
    finance_contact_name: '',
    finance_contact_phone: '',
    finance_contact_email: '',
  });
  const [entAttachments, setEntAttachments] = useState<AttachmentItem[]>([]);

  // 个人结算资料表单及附件
  const [personalForm, setPersonalForm] = useState({
    bank_account_name: '',
    bank_name: '',
    bank_branch_name: '',
    bank_account_no: '',
    recipient_name: '',
    tax_material: '',
  });
  const [personalAttachments, setPersonalAttachments] = useState<AttachmentItem[]>([]);

  /**
   * 加载数据（结合当前账户类型加载认证状态与结算资料）
   */
  const loadData = useCallback(async (type: 'enterprise' | 'personal') => {
    setLoading(true);
    setProfileStatus('');
    setAuditRemark('');

    try {
      if (type === 'enterprise') {
        // 1. 获取企业认证状态
        const certRes = await fetchGetEnterpriseMarketOnboarding().catch(() => null);
        const cert = certRes?.enterprise_certification;
        const currentCertStatus = mapApiStatusToCertStatus(cert?.certification_status);
        setCertStatus(currentCertStatus);
        setCertRejectReason(cert?.audit_remark || '');

        // 2. 获取企业结算资料
        const profileRes = await fetchEnterpriseSettlementProfile().catch(() => null);
        if (profileRes) {
          setProfileStatus(profileRes.status || '');
          setAuditRemark(profileRes.audit_remark || '');
          if (profileRes.enterprise) {
            setEntForm({
              bank_account_name: profileRes.enterprise.bank_account_name || profileRes.enterprise.settlement_subject_name || '',
              bank_name: profileRes.enterprise.bank_name || '',
              bank_branch_name: profileRes.enterprise.bank_branch_name || '',
              bank_account_no: profileRes.enterprise.bank_account_no || '',
              tax_no: profileRes.enterprise.tax_no || '',
              invoice_title: profileRes.enterprise.invoice_title || '',
              invoice_address_phone: profileRes.enterprise.invoice_address_phone || '',
              finance_contact_name: profileRes.enterprise.finance_contact_name || '',
              finance_contact_phone: profileRes.enterprise.finance_contact_phone || '',
              finance_contact_email: profileRes.enterprise.finance_contact_email || '',
            });

            if (profileRes.enterprise.attachments) {
              setEntAttachments(
                profileRes.enterprise.attachments.map((att, idx) => ({
                  id: att.id,
                  display_name: att.display_name || `企业资质附件_${idx + 1}`,
                  upload_asset_id: att.upload_asset_id || att.id || '',
                  visibility: (att.visibility as any) || 'FRONTEND_VISIBLE',
                }))
              );
            }
          }
        }
      } else {
        // 1. 获取个人认证状态
        const certRes = await fetchGetPersonalCertification().catch(() => null);
        const currentCertStatus = mapApiStatusToCertStatus(certRes?.certification_status);
        setCertStatus(currentCertStatus);
        setCertRejectReason(certRes?.audit_remark || '');

        // 2. 获取个人结算资料
        const profileRes = await fetchPersonalSettlementProfile().catch(() => null);
        if (profileRes) {
          setProfileStatus(profileRes.status || '');
          setAuditRemark(profileRes.audit_remark || '');
          if (profileRes.personal) {
            setPersonalForm({
              bank_account_name: profileRes.personal.bank_account_name || '',
              bank_name: profileRes.personal.bank_name || '',
              bank_branch_name: profileRes.personal.bank_branch_name || '',
              bank_account_no: profileRes.personal.bank_account_no || '',
              recipient_name: profileRes.personal.recipient_name || '',
              tax_material: profileRes.personal.tax_material || '',
            });

            if (profileRes.personal.attachments) {
              setPersonalAttachments(
                profileRes.personal.attachments.map((att, idx) => ({
                  id: att.id,
                  display_name: att.display_name || `个人证明附件_${idx + 1}`,
                  upload_asset_id: att.upload_asset_id || att.id || '',
                  visibility: (att.visibility as any) || 'FRONTEND_VISIBLE',
                }))
              );
            }
          }
        }
      }
    } catch (error) {
      console.error('加载结算资料或认证信息失败', error);
      setToast({ message: '获取结算资料失败，请稍后重试', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(accountType);
  }, [accountType, loadData]);

  /**
   * 上传附件文件处理
   */
  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    targetType: 'enterprise' | 'personal'
  ) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingAttachment(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const res = await uploadFile(file, 'COMMON', 'uploader-only');
        const assetId = String(res.asset_id || res.upload_asset_id || '');

        const newItem: AttachmentItem = {
          display_name: file.name,
          upload_asset_id: assetId,
          visibility: 'FRONTEND_VISIBLE',
        };

        if (targetType === 'enterprise') {
          setEntAttachments((prev) => [...prev, newItem]);
        } else {
          setPersonalAttachments((prev) => [...prev, newItem]);
        }
      }
      setToast({ message: '附件上传成功', type: 'success' });
    } catch (error: any) {
      console.error('附件上传失败', error);
      setToast({ message: error?.message || '附件上传失败，请稍后重试', type: 'error' });
    } finally {
      setUploadingAttachment(false);
      e.target.value = '';
    }
  };

  /**
   * 删除指定的已上传附件
   */
  const handleRemoveAttachment = (index: number, targetType: 'enterprise' | 'personal') => {
    if (targetType === 'enterprise') {
      setEntAttachments((prev) => prev.filter((_, i) => i !== index));
    } else {
      setPersonalAttachments((prev) => prev.filter((_, i) => i !== index));
    }
  };

  /**
   * 打开审核记录弹窗并获取数据
   */
  const handleOpenReviewRecords = async () => {
    setShowReviewModal(true);
    setLoadingRecords(true);
    try {
      const res =
        accountType === 'enterprise'
          ? await fetchEnterpriseSettlementProfileReviewRecords()
          : await fetchPersonalSettlementProfileReviewRecords();
      setReviewRecords(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error('获取结算资料审核记录失败', err);
      setToast({ message: '获取审核记录失败', type: 'error' });
    } finally {
      setLoadingRecords(false);
    }
  };

  /**
   * 提交或更新结算资料
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 校验实名认证状态：非 approved 状态禁止提交
    if (certStatus !== 'approved') {
      const label = accountType === 'enterprise' ? '企业' : '个人';
      setToast({
        message: `请先完成${label}实名认证，认证通过后方可提交或更新结算资料`,
        type: 'warning',
      });
      return;
    }

    setSubmitting(true);

    try {
      if (accountType === 'enterprise') {
        // 必填项校验
        if (
          !entForm.bank_account_name.trim() ||
          !entForm.bank_name.trim() ||
          !entForm.bank_account_no.trim() ||
          !entForm.tax_no.trim() ||
          !entForm.invoice_title.trim() ||
          !entForm.invoice_address_phone.trim() ||
          !entForm.finance_contact_name.trim() ||
          !entForm.finance_contact_phone.trim()
        ) {
          setToast({ message: '请完整填写企业结算资料必填项', type: 'warning' });
          setSubmitting(false);
          return;
        }

        const attachmentRequests: Api.Revenue.AttachmentRequest[] = entAttachments.map((att, idx) => ({
          display_name: att.display_name,
          sort_no: idx + 1,
          upload_asset_id: att.upload_asset_id,
          visibility: att.visibility || 'FRONTEND_VISIBLE',
        }));

        await fetchUpdateEnterpriseSettlementProfile({
          bank_account_name: entForm.bank_account_name.trim(),
          bank_name: entForm.bank_name.trim(),
          bank_branch_name: entForm.bank_branch_name.trim() || undefined,
          bank_account_no: entForm.bank_account_no.trim(),
          tax_no: entForm.tax_no.trim(),
          invoice_title: entForm.invoice_title.trim(),
          invoice_address_phone: entForm.invoice_address_phone.trim(),
          finance_contact_name: entForm.finance_contact_name.trim(),
          finance_contact_phone: entForm.finance_contact_phone.trim(),
          finance_contact_email: entForm.finance_contact_email.trim() || undefined,
          attachments: attachmentRequests.length > 0 ? attachmentRequests : undefined,
        });
      } else {
        // 个人必填项校验
        if (
          !personalForm.bank_account_name.trim() ||
          !personalForm.bank_name.trim() ||
          !personalForm.bank_account_no.trim() ||
          !personalForm.recipient_name.trim() ||
          !personalForm.tax_material.trim()
        ) {
          setToast({ message: '请完整填写个人结算资料必填项', type: 'warning' });
          setSubmitting(false);
          return;
        }

        const attachmentRequests: Api.Revenue.AttachmentRequest[] = personalAttachments.map((att, idx) => ({
          display_name: att.display_name,
          sort_no: idx + 1,
          upload_asset_id: att.upload_asset_id,
          visibility: att.visibility || 'FRONTEND_VISIBLE',
        }));

        await fetchUpdatePersonalSettlementProfile({
          bank_account_name: personalForm.bank_account_name.trim(),
          bank_name: personalForm.bank_name.trim(),
          bank_branch_name: personalForm.bank_branch_name.trim() || undefined,
          bank_account_no: personalForm.bank_account_no.trim(),
          recipient_name: personalForm.recipient_name.trim(),
          tax_material: personalForm.tax_material.trim(),
          attachments: attachmentRequests.length > 0 ? attachmentRequests : undefined,
        });
      }

      setToast({ message: '结算资料保存成功！', type: 'success' });
      // 重新加载资料
      await loadData(accountType);
    } catch (error: any) {
      console.error('保存结算资料失败', error);
      setToast({
        message: error?.message || error?.msg || '保存失败，请检查填写内容或重试',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isCertified = certStatus === 'approved';
  const profileStatusBadge = profileStatusConfig[profileStatus.toUpperCase()] || null;

  return (
    <AccountLayout
      activePath="/billing-profile"
      breadcrumbs={[{ label: '账号中心', href: '/profile' }, { label: '结算资料' }]}
    >
      <div className="glass-card rounded-2xl p-6 md:p-8 space-y-6">
        {/* 头部标题与资料状态 */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-background-300/40 pb-5">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading font-bold text-lg text-foreground-950">结算资料</h1>
              {profileStatusBadge && (
                <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${profileStatusBadge.className}`}>
                  {profileStatusBadge.label}
                </span>
              )}
            </div>
            <p className="text-xs text-foreground-500 mt-1">请填写准确的收款账户与发票/报税信息，用于收益结算付款</p>
          </div>

          {/* 右侧功能按钮与当前账号类型 */}
          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={handleOpenReviewRecords}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-white/80 hover:bg-white text-foreground-700 border border-background-300/60 shadow-sm transition-all cursor-pointer hover:text-foreground-950"
            >
              <i className="ri-history-line text-sm text-foreground-500"></i>
              审核记录
            </button>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-primary-500/10 text-foreground-900 border border-primary-500/30">
              <i className={accountType === 'enterprise' ? 'ri-building-line text-sm text-primary-600' : 'ri-user-3-line text-sm text-primary-600'}></i>
              {accountType === 'enterprise' ? '对公账户 (企业账号)' : '个人账户 (个人账号)'}
            </span>
          </div>
        </div>

        {/* 实名认证状态提示面板 */}
        {loading ? (
          <div className="flex items-center justify-center py-8 text-foreground-400 text-sm gap-2">
            <i className="ri-loader-4-line animate-spin text-lg"></i>
            正在加载结算资料与认证状态...
          </div>
        ) : (
          <>
            {!isCertified ? (
              <div className="p-4 rounded-xl border border-amber-200 bg-amber-50/80 text-amber-900 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <i className="ri-error-warning-fill text-amber-500 text-xl shrink-0 mt-0.5"></i>
                  <div>
                    <h4 className="text-sm font-semibold">
                      未完成{accountType === 'enterprise' ? '企业' : '个人'}实名认证
                    </h4>
                    <p className="text-xs text-amber-700 mt-1">
                      根据平台资金合规要求，提交或更新{accountType === 'enterprise' ? '对公' : '个人'}结算资料前，必须先完成对应的{accountType === 'enterprise' ? '企业认证' : '个人实名认证'}。
                      {certRejectReason && <span className="block text-rose-600 mt-1">驳回原因：{certRejectReason}</span>}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/certification')}
                  className="px-4 py-2 rounded-lg bg-amber-500 text-foreground-950 hover:bg-amber-400 text-xs font-semibold shrink-0 transition-all cursor-pointer flex items-center gap-1 shadow-sm"
                >
                  前往实名认证
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            ) : (
              <div className="p-3.5 rounded-xl border border-emerald-200/80 bg-emerald-50/60 text-emerald-900 flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-emerald-500 text-lg shrink-0"></i>
                <span className="text-xs font-medium">
                  您已通过{accountType === 'enterprise' ? '企业实名认证' : '个人实名认证'}，可正常填写或更新结算资料。
                </span>
              </div>
            )}

            {/* 审核驳回提示 */}
            {auditRemark && profileStatus.toUpperCase() === 'REJECTED' && (
              <div className="p-3.5 rounded-xl border border-rose-200 bg-rose-50/80 text-rose-900 flex items-start gap-3">
                <i className="ri-close-circle-fill text-rose-500 text-lg shrink-0 mt-0.5"></i>
                <div className="text-xs">
                  <span className="font-semibold block mb-0.5">结算资料审核未通过</span>
                  <p>{auditRemark}</p>
                </div>
              </div>
            )}

            {/* 表单内容 */}
            <form onSubmit={handleSubmit} className="space-y-5 pt-2">
              {accountType === 'enterprise' ? (
                // 企业结算资料表单
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                        开户银行户名 <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={entForm.bank_account_name}
                        onChange={(e) => setEntForm({ ...entForm, bank_account_name: e.target.value })}
                        placeholder="需与企业认证名称一致"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                        纳税人识别号/税号 <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={entForm.tax_no}
                        onChange={(e) => setEntForm({ ...entForm, tax_no: e.target.value })}
                        placeholder="请输入企业统一社会信用代码/税号"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                        开户银行 <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={entForm.bank_name}
                        onChange={(e) => setEntForm({ ...entForm, bank_name: e.target.value })}
                        placeholder="如 中国工商银行"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">开户支行</label>
                      <input
                        type="text"
                        value={entForm.bank_branch_name}
                        onChange={(e) => setEntForm({ ...entForm, bank_branch_name: e.target.value })}
                        placeholder="如 北京朝阳支行（选填）"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                      银行账号 <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={entForm.bank_account_no}
                      onChange={(e) => setEntForm({ ...entForm, bank_account_no: e.target.value })}
                      placeholder="请输入企业对公银行账号"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                        发票抬头 <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={entForm.invoice_title}
                        onChange={(e) => setEntForm({ ...entForm, invoice_title: e.target.value })}
                        placeholder="请输入开票抬头"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                        发票地址及电话 <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={entForm.invoice_address_phone}
                        onChange={(e) => setEntForm({ ...entForm, invoice_address_phone: e.target.value })}
                        placeholder="注册地址及联系电话"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                        财务联系人姓名 <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={entForm.finance_contact_name}
                        onChange={(e) => setEntForm({ ...entForm, finance_contact_name: e.target.value })}
                        placeholder="请输入联系人姓名"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                        财务联系人手机号 <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={entForm.finance_contact_phone}
                        onChange={(e) => setEntForm({ ...entForm, finance_contact_phone: e.target.value })}
                        placeholder="请输入手机号"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">财务联系人邮箱</label>
                      <input
                        type="email"
                        value={entForm.finance_contact_email}
                        onChange={(e) => setEntForm({ ...entForm, finance_contact_email: e.target.value })}
                        placeholder="常用接收账单邮箱（选填）"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                      />
                    </div>
                  </div>
                </>
              ) : (
                // 个人结算资料表单
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                        收款人真实姓名 <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={personalForm.recipient_name}
                        onChange={(e) => setPersonalForm({ ...personalForm, recipient_name: e.target.value })}
                        placeholder="需与实名认证姓名一致"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                        开户银行户名 <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={personalForm.bank_account_name}
                        onChange={(e) => setPersonalForm({ ...personalForm, bank_account_name: e.target.value })}
                        placeholder="请输入开户户名"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                        开户银行 <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={personalForm.bank_name}
                        onChange={(e) => setPersonalForm({ ...personalForm, bank_name: e.target.value })}
                        placeholder="如 招商银行"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">开户支行</label>
                      <input
                        type="text"
                        value={personalForm.bank_branch_name}
                        onChange={(e) => setPersonalForm({ ...personalForm, bank_branch_name: e.target.value })}
                        placeholder="如 深圳高新园支行（选填）"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                      银行账号 <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={personalForm.bank_account_no}
                      onChange={(e) => setPersonalForm({ ...personalForm, bank_account_no: e.target.value })}
                      placeholder="请输入个人银行卡号"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                      身份证件号 / 报税材料说明 <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={personalForm.tax_material}
                      onChange={(e) => setPersonalForm({ ...personalForm, tax_material: e.target.value })}
                      placeholder="请输入身份证号或个人劳务报税材料说明"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                    />
                  </div>
                </>
              )}

              {/* 资质与证明附件上传区域 */}
              <div className="pt-4 border-t border-background-200/80 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-xs font-semibold text-foreground-900">
                      {accountType === 'enterprise' ? '企业资质及证明附件' : '个人证明及报税附件'} (选填)
                    </label>
                    <p className="text-[11px] text-foreground-400 mt-0.5">
                      {accountType === 'enterprise'
                        ? '支持上传营业执照副本、开户许可证、盖章授权书等资质材料'
                        : '支持上传身份证件照片、个人银行卡照片或劳务报税相关证明'}
                    </p>
                  </div>
                  <label
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-white border border-background-300 hover:bg-background-50 text-foreground-800 transition-all cursor-pointer shadow-sm ${uploadingAttachment || !isCertified ? 'opacity-60 pointer-events-none' : ''
                      }`}
                  >
                    {uploadingAttachment ? (
                      <i className="ri-loader-4-line animate-spin text-primary-500"></i>
                    ) : (
                      <i className="ri-upload-cloud-2-line text-primary-500 text-sm"></i>
                    )}
                    {uploadingAttachment ? '上传中...' : '上传附件'}
                    <input
                      type="file"
                      multiple
                      onChange={(e) => handleFileUpload(e, accountType)}
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx"
                      disabled={uploadingAttachment || !isCertified}
                    />
                  </label>
                </div>

                {/* 已上传附件列表 */}
                {(accountType === 'enterprise' ? entAttachments : personalAttachments).length === 0 ? (
                  <div className="p-4 rounded-xl border border-dashed border-background-300 text-center text-foreground-400 text-xs bg-background-50/40">
                    <i className="ri-attachment-line text-lg mb-1 block opacity-50"></i>
                    暂未上传任何资质证明附件
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(accountType === 'enterprise' ? entAttachments : personalAttachments).map((att, index) => (
                      <div
                        key={att.id || att.upload_asset_id || index}
                        className="flex items-center justify-between p-2.5 rounded-xl border border-background-300/60 bg-white/80 text-xs shadow-sm hover:border-primary-400/40 transition-all"
                      >
                        <div className="flex items-center gap-2.5 truncate">
                          <i className="ri-file-text-line text-primary-500 text-base shrink-0"></i>
                          <span className="font-medium text-foreground-900 truncate max-w-[200px] sm:max-w-[340px]">
                            {att.display_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <select
                            value={att.visibility}
                            onChange={(e) => {
                              const val = e.target.value as Api.Revenue.AttachmentVisibility;
                              if (accountType === 'enterprise') {
                                setEntAttachments((prev) =>
                                  prev.map((item, i) => (i === index ? { ...item, visibility: val } : item))
                                );
                              } else {
                                setPersonalAttachments((prev) =>
                                  prev.map((item, i) => (i === index ? { ...item, visibility: val } : item))
                                );
                              }
                            }}
                            className="px-2 py-1 text-[11px] rounded-lg border border-background-300/60 bg-background-50 text-foreground-700 outline-none cursor-pointer"
                          >
                            <option value="FRONTEND_VISIBLE">前台可见</option>
                            <option value="INTERNAL_ONLY">仅内部审核可见</option>
                          </select>
                          <button
                            type="button"
                            onClick={() => handleRemoveAttachment(index, accountType)}
                            className="p-1 text-foreground-400 hover:text-rose-600 rounded-lg transition-colors cursor-pointer"
                            title="删除附件"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 提交按钮区域 */}
              <div className="pt-4 flex items-center gap-4">
                <button
                  type="submit"
                  disabled={submitting || !isCertified}
                  className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 cursor-pointer ${!isCertified
                      ? 'bg-gray-200 text-foreground-400 cursor-not-allowed border border-gray-300/50'
                      : submitting
                        ? 'bg-primary-400 text-foreground-950 cursor-wait'
                        : 'bg-primary-500 text-foreground-950 hover:bg-primary-400 shadow-md shadow-primary-500/20'
                    }`}
                >
                  {submitting && <i className="ri-loader-4-line animate-spin text-base"></i>}
                  {submitting ? '正在保存...' : '保存结算资料'}
                </button>
                {!isCertified && (
                  <span className="text-xs text-amber-600 flex items-center gap-1">
                    <i className="ri-lock-line"></i> 需先完成实名认证后解锁保存功能
                  </span>
                )}
              </div>
            </form>
          </>
        )}
      </div>

      {/* 审核记录弹窗 */}
      {showReviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden border border-background-300/60 flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
              <h3 className="font-heading font-bold text-base text-foreground-950 flex items-center gap-2">
                <i className="ri-history-line text-primary-500"></i>
                结算资料审核记录
              </h3>
              <button
                type="button"
                onClick={() => setShowReviewModal(false)}
                className="p-1 rounded-lg text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4 flex-1">
              {loadingRecords ? (
                <div className="flex items-center justify-center py-8 text-foreground-400 text-xs gap-2">
                  <i className="ri-loader-4-line animate-spin text-base"></i>
                  正在加载审核记录...
                </div>
              ) : reviewRecords.length === 0 ? (
                <div className="text-center py-8 text-foreground-400 text-xs">
                  <i className="ri-file-search-line text-3xl mb-2 block opacity-40"></i>
                  暂无结算资料审核记录
                </div>
              ) : (
                <div className="relative border-l-2 border-background-200 ml-3 space-y-6 pl-4 py-1">
                  {reviewRecords.map((item, idx) => {
                    const resUpper = (item.result || '').toUpperCase();
                    const isPassed = resUpper === 'APPROVED' || resUpper === 'PASSED' || resUpper === 'SUCCESS';
                    const isRejected = resUpper === 'REJECTED' || resUpper === 'FAILED';

                    return (
                      <div key={item.id || idx} className="relative">
                        <span
                          className={`absolute -left-[23px] top-0.5 w-3 h-3 rounded-full border-2 border-white ${isPassed ? 'bg-emerald-500' : isRejected ? 'bg-rose-500' : 'bg-amber-500'
                            }`}
                        ></span>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${isPassed
                                ? 'bg-emerald-100 text-emerald-700'
                                : isRejected
                                  ? 'bg-rose-100 text-rose-700'
                                  : 'bg-amber-100 text-amber-700'
                              }`}
                          >
                            {isPassed ? '审核通过' : isRejected ? '审核驳回' : item.result || '提交审核'}
                          </span>
                          <span className="text-[11px] text-foreground-400">
                            {item.reviewed_at || item.created_at || '-'}
                          </span>
                        </div>
                        {item.review_remark && (
                          <p className="text-xs text-foreground-700 bg-background-50 p-2.5 rounded-lg border border-background-200/60 mt-2">
                            <span className="text-foreground-400 font-medium">审核说明：</span>
                            {item.review_remark}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-background-200 bg-background-50/50 flex justify-end">
              <button
                type="button"
                onClick={() => setShowReviewModal(false)}
                className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-background-200 text-foreground-800 hover:bg-background-300 transition-colors cursor-pointer"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 消息 Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </AccountLayout>
  );
}

